import { Component, ViewEncapsulation, HostBinding, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription, Observable, debounceTime, distinctUntilChanged, startWith, tap, map } from 'rxjs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { LoadingSpinnerComponent } from '../../utils/loading-spinner.component';
import { LoginService } from '../../../services/login.service';
import { UserVisibility } from '../../../models/user';
import { UserService } from '../../../services/user.service';
import { simpleTransitions } from '../../../shared/animations/simple-transitions';

@Component({
	selector: 'copo-select-visibility',
	templateUrl: './select-visibility.component.html',
	animations: simpleTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrls: ['./select-visibility.component.css'],
	standalone: true,
	imports: [
		NgIf,
		TranslateModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyCheckboxModule,
		ReactiveFormsModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyFormFieldModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyInputModule,
		// eslint-disable-next-line deprecation/deprecation
		MatLegacyButtonModule,
		MatIconModule,
		CdkScrollable,
		VirtualScrollerDirective,
		NgFor,
		LoadingSpinnerComponent,
		RouterLink
	]
})
export class SelectVisibilityComponent implements OnDestroy {
	@HostBinding('class.mat-typography') matTypography = true;
	@HostBinding('class.full-height') fullHeight = true;
	searchControl = new FormControl('', { nonNullable: true });
	readOnlyFormControl: FormControl<boolean>;
	searchSub: Subscription;
	selectedVisi: UserVisibility | undefined;
	state: string | undefined;
	responseType: string | undefined;
	redirectUri: string | undefined;
	clientId: string | undefined;
	codeChallenge: string | undefined;
	codeChallengeMethod: string | undefined;
	query = '';
	currentTime = Date.now();
	willLoad = false;
	hasDelegation = false;
	submitting = false;
	submittingTimeout: number | undefined;

	constructor(
		private readonly userService: UserService,
		private readonly route: ActivatedRoute,
		private readonly loginService: LoginService
	) {
		let prod = false;
		switch (location.origin) {
		case 'https://auth.steeluser.com':
		case 'https://web-auth-ope-prd.steeluser.com':
			prod = true;
			break;
		}
		this.readOnlyFormControl = new FormControl(prod, { nonNullable: true });
		if (prod) {
			this.readOnlyFormControl.disable();
		}
		this.searchSub = this.searchControl.valueChanges.pipe(
			startWith(this.query),
			tap(() => {
				this.willLoad = true;
			}),
			debounceTime(500),
			tap(() => {
				this.willLoad = false;
			}),
			distinctUntilChanged(),
		).subscribe({
			next: query => {
				this.query = query;
			}
		});
		const params = <Record<string, string | undefined>> this.route.snapshot.queryParams;
		this.state = params['state'];
		this.responseType = params['response_type'];
		this.redirectUri = params['redirect_uri'];
		this.clientId = params['client_id'];
		this.codeChallenge = params['code_challenge'];
		this.codeChallengeMethod = params['code_challenge_method'];
		if (this.clientId) {
			return;
		}
		this.clientId = '7U2EyJ8Xn5UvRPWA';
		this.responseType = 'code';
		this.state = '/';
	}

	get dataId() {
		return `visi-${ this.query }-${ this.currentTime }`;
	}

	logoutUrl() {
		const logoutUrl = new URL('/rest/auth/logout', location.origin);
		const logoutParams = logoutUrl.searchParams;
		const callbackUrl = this.getAuthRequestURL();
		logoutParams.set('callback', String(callbackUrl));
		return logoutUrl;
	}

	getAuthRequestURL() {
		return this.loginService
			.createUrl(
				'/rest/auth/oauth2',
				this.state,
				this.responseType,
				this.redirectUri,
				this.clientId,
				this.codeChallenge,
				this.codeChallengeMethod
			);
	}

	onMoreDataNeeded(
		index: number,
		limit: number,
		provideData: (obs: Observable<UserVisibility[]>) => void
	) {
		const { query } = this;
		provideData(
			this.userService.searchVisibility(query, Math.floor(index / limit) + 1, limit)
				.pipe(
					tap(v => {
						this.hasDelegation = v.hasDelegation;
					}),
					map(v => v.results)
				)
		);
	}

	simulate() {
		if (!this.selectedVisi) {
			return;
		}
		const authUrl = this.getAuthRequestURL();
		authUrl.searchParams.set('selected_user', String(this.selectedVisi.userId));
		authUrl.searchParams.set('selected_visibility', String(this.selectedVisi.visibilityGroupId));
		authUrl.searchParams.set('read_only', String(this.readOnlyFormControl.value));
		this.submitting = true;
		location.replace(authUrl.toString());
		this.reEnableAfter(30_000);
	}

	logout() {
		this.submitting = true;
		location.replace(this.logoutUrl().toString());
		this.reEnableAfter(30_000);
	}

	ngOnDestroy() {
		this.searchSub.unsubscribe();
	}

	getLatests(list: UserVisibility[], isLatest: boolean) {
		const res = list.filter(
			isLatest
				? v => v.latestOrder > 1
				: v => v.latestOrder === 0
		);
		if (res.length === 0) {
			return undefined;
		}
		return res;
	}

	private reEnableAfter(time: number) {
		this.submittingTimeout = setTimeout(
			() => {
				this.submittingTimeout = undefined;
				this.submitting = false;
			},
			time
		);
	}
}
